import { LeftOutlined } from "@ant-design/icons";
import {
  Col,
  Drawer,
  Row,
  Table,
  Typography,
  Image,
  Space,
  Tag,
} from "antd";
import React, { useEffect, useState } from "react";
import { frequencyDisplay } from "../../../helpers/medicineKeys";
import { getPatientMedicationHistoryAPI } from "../../../services/patient.services";
import dayjs from "dayjs";
import bedImg from "../../../assets/images/patientIcons/bed.svg";
import useIsMobile from "../../../hooks/useIsMobile";
import "../NurseMedicine/NurseMedicine.css";

const MedicationHistoryChart = ({
  open,
  setOpen,
  params,
  patientData,
}) => {
  const isMobile = useIsMobile();
  const [medHistoryData, setMedHistoryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const ip =
    params?.patient ||
    patientData?.ip_admission ||
    patientData?.id ||
    patientData?.admission_id;

  useEffect(() => {
    if (!open || !ip) {
      if (!ip) {
        setMedHistoryData([]);
        setLoading(false);
      }
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setMedHistoryData([]);

    getPatientMedicationHistoryAPI(ip)
      .then((res) => {
        if (cancelled) return;
        const payload = res?.data?.data ?? res?.data ?? [];
        setMedHistoryData(Array.isArray(payload) ? payload : []);
      })
      .catch((error) => {
        if (cancelled) return;
        console.log(`Error: ${error}`);
        setMedHistoryData([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [ip, open]);

  const extractTimeSlots = (historyData = []) =>
    [
      ...new Set(
        historyData?.flatMap((medicine) =>
          medicine?.actions?.map((action) => action.date)
        )
      ),
    ].sort((a, b) => new Date(a) - new Date(b));

  const timeSlots =
    medHistoryData?.length > 0 ? extractTimeSlots(medHistoryData) : [];

  const patientName = `${patientData?.patient?.first_name || ""} ${
    patientData?.patient?.last_name || ""
  }`.trim();

  const renderDrawerTitle = () => {
    if (isMobile) {
      return (
        <div className="nurse-med-history-drawer-header">
          <div className="nurse-med-history-drawer-header__top">
            <LeftOutlined
              className="nurse-med-history-drawer-header__back"
              onClick={() => setOpen(false)}
            />
            <div className="nurse-med-history-drawer-header__patient">
              <Typography.Text className="nurse-med-history-drawer-header__name">
                {patientName || "-"}
              </Typography.Text>
              <Space size={6} className="nurse-med-history-drawer-header__tags">
                {patientData?.bed?.bed_no && (
                  <Tag color="orange" style={{ fontWeight: 500 }}>
                    <Image src={bedImg} preview={false} />{" "}
                    {patientData?.bed?.bed_no}
                  </Tag>
                )}
                <Tag>
                  {patientData?.patient?.age} {patientData?.patient?.gender}
                </Tag>
                {patientData?.ip_id && <Tag>IP ID: {patientData?.ip_id}</Tag>}
              </Space>
            </div>
          </div>
          <Typography.Title
            level={5}
            className="nurse-med-history-drawer-header__title"
          >
            Medication History
          </Typography.Title>
        </div>
      );
    }

    return (
      <Row style={{ padding: "12px 14px" }}>
        <Col>
          <LeftOutlined
            onClick={() => setOpen(false)}
            style={{
              fontSize: "18px",
              padding: "0px 10px",
              marginTop: "15px",
            }}
          />
        </Col>
        <Col span={10} style={{ marginLeft: "10px" }}>
          <Space>
            <Typography
              style={{ color: "#101828", fontSize: "16px", fontWeight: 600 }}
            >
              {patientName || "-"}
            </Typography>
            {patientData?.bed?.bed_no && (
              <Tag color="orange" style={{ fontWeight: "500" }}>
                <Image src={bedImg} preview={false} /> {patientData?.bed?.bed_no}
              </Tag>
            )}
          </Space>
          <br />
          <Space style={{ fontWeight: "500" }}>
            <Tag>
              {patientData?.patient?.age} {patientData?.patient?.gender}
            </Tag>
            {patientData?.ip_id && <Tag>IP ID: {patientData?.ip_id}</Tag>}
          </Space>
        </Col>
        <Col span={12} className="flex-start">
          <Typography
            style={{
              marginLeft: "12px",
              fontWeight: "500",
              fontSize: "20px",
            }}
          >
            Medication History
          </Typography>
        </Col>
      </Row>
    );
  };

  const columns = [
    {
      title: "Medication/Dose/Route",
      fixed: "left",
      render(value, record) {
        return {
          children: (
            <Space>
              <Typography>{record?.name || record?.medicine_name}</Typography>
              <Typography>{record?.dose}</Typography>
              {record?.route && <Tag>{record?.route}</Tag>}
            </Space>
          ),
        };
      },
      width: 400,
    },
    ...timeSlots?.map((slot) => ({
      title: `${dayjs(slot).format("MMM DD").toUpperCase()}`,
      dataIndex: slot,
      width: 160,
      render: (_, record) => {
        const action = record?.actions?.find((a) => a?.date === slot);
        return <>{action ? frequencyDisplay?.[action?.frequency] : "-"}</>;
      },
    })),
  ];

  return (
    <Drawer
      className="nurse-med-history-drawer"
      open={open}
      width={"100%"}
      styles={{ header: { padding: isMobile ? "10px 12px" : 0 } }}
      onClose={() => setOpen(false)}
      title={renderDrawerTitle()}
      closeIcon={null}
      // Nested under Initial Assessment drawer — render on body so Android
      // WebViews don't blank out the history table.
      getContainer={() => document.body}
      zIndex={1200}
      destroyOnClose
    >
      <div className="nurse-med-history-table-wrap">
        <Table
          className="nurse-med-history-table"
          dataSource={medHistoryData}
          loading={loading}
          locale={{
            emptyText: loading ? " " : "No Data",
          }}
          scroll={{
            x: isMobile
              ? Math.max(480, 280 + (timeSlots?.length || 0) * 120)
              : 1200,
            ...(isMobile ? {} : { y: "60vh" }),
          }}
          pagination={false}
          columns={columns}
          bordered
          size={isMobile ? "small" : "middle"}
          rowKey={(record, index) =>
            record?.id ||
            `${record?.medicine_name || record?.name || "med"}-${index}`
          }
        />
      </div>
    </Drawer>
  );
};

export default MedicationHistoryChart;
